
import React from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { Story } from '@/utils/storiesData';
import HorizontalScroller from '@/components/mobile/HorizontalScroller';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useIsMobile } from '@/hooks/use-mobile';

interface FeaturedStoriesCarouselProps {
  stories: Story[];
}

const FeaturedStoriesCarousel: React.FC<FeaturedStoriesCarouselProps> = ({ stories }) => {
  const isMobile = useIsMobile();
  
  if (stories.length === 0) return null;

  if (isMobile) {
    return (
      <HorizontalScroller showNextCard={true} snap>
        {stories.map(story => (
          <Link 
            key={story.id}
            to={`/stories/${story.slug}`} 
            className="flex-shrink-0 w-[240px] snap-start"
          >
            <div className="relative overflow-hidden rounded-[2px] aspect-[4/3]">
              <img 
                src={story.coverImage} 
                alt={story.title} 
                className="w-full h-full object-cover" 
              />
            </div>
            <div className="mt-2">
              <span className="bg-primary text-primary-foreground text-[10px] font-normal px-2 py-0.5 rounded-[30px] mr-2">
                {story.category}
              </span>
              <span className="text-[10px] flex items-center inline-block">
                <Clock size={10} className="mr-1" />
                {story.readTime} min
              </span>
              <h3 className="text-sm mt-1 font-chroma line-clamp-2">
                {story.title}
              </h3>
            </div>
          </Link>
        ))}
      </HorizontalScroller>
    );
  }

  return (
    <Carousel 
      className="w-full" 
      opts={{
        align: "start",
        slidesToScroll: 1
      }}
    >
      <CarouselContent className="-ml-4">
        {stories.map(story => (
          <CarouselItem 
            key={story.id} 
            className="pl-4 sm:basis-1/2 lg:basis-1/4"
          >
            <Link to={`/stories/${story.slug}`} className="block h-full group">
              <div className="relative overflow-hidden rounded-[2px] aspect-[3/2]">
                <img 
                  src={story.coverImage} 
                  alt={story.title} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center mb-3">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-[30px]">
                      {story.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-normal text-white mb-2 group-hover:text-primary transition-colors font-chroma">
                    {story.title}
                  </h3>
                  <p className="text-gray-300 text-sm flex items-center">
                    <Clock size={14} className="mr-1" />
                    {story.readTime} min read
                  </p>
                </div>
              </div>
            </Link>
          </CarouselItem>
        ))}
      </CarouselContent>
      <div className="hidden sm:block">
        <CarouselPrevious className="-left-4 bg-background/80 backdrop-blur-sm border-none text-white" />
        <CarouselNext className="-right-4 bg-background/80 backdrop-blur-sm border-none text-white" />
      </div>
    </Carousel>
  );
};

export default FeaturedStoriesCarousel;
